import { useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDateTime, shortId } from "../../lib/format";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useAdminAccounts, useJournal, type AccountView, type AuditEvent } from "../access/api";
import { readFailure } from "../access/failure";
import { STATE_LABEL_KEY } from "../admin-users/accounts";
import { roleLabel, sortedRoles } from "../admin-users/roles";
import { ACTION_KEY, createdRoles, fieldChanges, inviteSent, isRefusal, otherDetails, type FieldChange } from "./journal";

/**
 * The account journal ([NF-017], [AC-067]): every change to accounts and
 * roles, every login, logout and elevation, every refused login - with acting
 * person, time and subject. Append-only; nothing here changes or deletes an
 * entry. Newest first, filterable to one account.
 *
 * The journal holds ids only. A subject account is shown by the display name
 * the administration sees; an id the administration no longer finds is a
 * former account ([NF-018]).
 */
export function AccessJournalPage(): JSX.Element {
  const { t } = useTranslation();
  const [accountId, setAccountId] = useState("");
  const journal = useJournal({ kind: accountId ? "account" : "", id: accountId });
  const accounts = useAdminAccounts();
  const byId = new Map((accounts.data ?? []).map((account) => [account.id, account]));

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 data-testid="admin-journal_heading" className="text-2xl font-semibold tracking-tight">
          {t("adminJournal.title")}
        </h1>
        <p className="max-w-3xl text-sm text-muted-foreground">{t("adminJournal.lead")}</p>
      </div>

      <div data-testid="admin-journal_filter" role="group" aria-label={t("adminJournal.filter")} className="flex flex-wrap items-end gap-3">
        <label className="space-y-1 text-sm">
          <span className="block font-medium">{t("adminJournal.account")}</span>
          <select
            data-testid="admin-journal_account"
            className="h-9 min-w-[16rem] rounded-md border border-input bg-background px-3 text-sm"
            value={accountId}
            onChange={(event) => setAccountId(event.target.value)}
          >
            <option value="">{t("adminJournal.allAccounts")}</option>
            {(accounts.data ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.display_name || account.email}
              </option>
            ))}
          </select>
        </label>
        <Button data-testid="admin-journal_reload" variant="outline" onClick={() => void journal.refetch()}>
          {t("common.retry")}
        </Button>
      </div>

      {journal.isError ? (
        <p data-testid="admin-journal_error" role="alert" className="text-sm text-destructive-text">
          {readFailure(journal.error, t("errors.load")).message}
        </p>
      ) : !journal.data ? (
        <p data-testid="admin-journal_loading" className="text-sm text-muted-foreground">
          {t("common.loading")}
        </p>
      ) : journal.data.length === 0 ? (
        <p data-testid="admin-journal_empty" className="rounded-lg border border-dashed px-6 py-10 text-center text-sm text-muted-foreground">
          {t("adminJournal.empty")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <Table data-testid="admin-journal_table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("adminJournal.colAt")}</TableHead>
                <TableHead>{t("adminJournal.colActor")}</TableHead>
                <TableHead>{t("adminJournal.colAction")}</TableHead>
                <TableHead>{t("adminJournal.colSubject")}</TableHead>
                <TableHead>{t("adminJournal.colDetails")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {journal.data.map((event) => (
                <TableRow key={event.id} data-testid={`admin-journal_row_${event.id}`} className={cn(isRefusal(event.action) && "bg-destructive/5")}>
                  <TableCell className="whitespace-nowrap text-sm">{formatDateTime(event.at)}</TableCell>
                  <TableCell className="text-sm">{event.actor}</TableCell>
                  <TableCell>
                    <div className="text-sm">{ACTION_KEY[event.action] ? t(ACTION_KEY[event.action] ?? "") : event.action}</div>
                    <div className="font-mono text-xs text-muted-foreground">{event.action}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    <Subject event={event} byId={byId} />
                  </TableCell>
                  <TableCell className="text-sm">
                    <Details event={event} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function Subject({ event, byId }: { event: AuditEvent; byId: Map<string, AccountView> }): JSX.Element {
  const { t } = useTranslation();
  if (event.subject_kind === "account") {
    const account = byId.get(event.subject_id);
    if (account) return <span>{account.display_name || account.email}</span>;
    return <span className="text-muted-foreground">{t("adminJournal.formerAccount")}</span>;
  }
  if (event.subject_kind === "login_session") {
    return <span>{t("adminJournal.session", { id: shortId(event.subject_id) })}</span>;
  }
  return <span className="font-mono text-xs">{`${event.subject_kind} ${shortId(event.subject_id)}`}</span>;
}

function Details({ event }: { event: AuditEvent }): JSX.Element | null {
  const { t } = useTranslation();
  const roles = (list: unknown): string =>
    Array.isArray(list) ? sortedRoles(list.filter((r): r is string => typeof r === "string")).map((r) => roleLabel(t, r)).join(", ") || t("common.none") : t("common.none");
  const state = (value: unknown): string =>
    value === "unconfirmed" || value === "active" || value === "retired" ? t(STATE_LABEL_KEY[value]) : String(value ?? t("common.none"));
  const show = (change: FieldChange): string => {
    if (change.field === "roles") return t("adminJournal.changeRoles", { from: roles(change.from), to: roles(change.to) });
    if (change.field === "state") return t("adminJournal.changeState", { from: state(change.from), to: state(change.to) });
    return t("adminJournal.changeAdmin", { from: String(change.from), to: String(change.to) });
  };

  const lines: string[] = [];
  if (event.action === "account.update") lines.push(...fieldChanges(event).map(show));
  if (event.action === "account.create") lines.push(t("adminJournal.createdRoles", { roles: roles(createdRoles(event)) }));
  if (event.action === "account.invite") {
    const sent = inviteSent(event);
    if (sent !== null) lines.push(sent ? t("adminJournal.inviteSent") : t("adminJournal.inviteNotSent"));
  }
  if (event.details.display_name_changed === true) lines.push(t("adminJournal.displayNameChanged"));
  const other = otherDetails(event);

  if (lines.length === 0 && !other) return null;
  return (
    <div className="space-y-0.5">
      {lines.map((line) => (
        <div key={line}>{line}</div>
      ))}
      {other ? <div className="break-all font-mono text-xs text-muted-foreground">{other}</div> : null}
    </div>
  );
}
